import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { previewCoupon } from '../api';

// Marketing codes. The server is the one that decides whether a code is
// valid, what it's worth, and whether it still has uses left - this only
// shows the answer. It's checked again when the order is placed.
//
// Note: error.message below comes straight from the backend's English
// HTTPException text - it isn't translated. Doing that needs the API to
// return stable error codes instead of English strings; see the note left
// in main.py for the same gap on the order-submission errors.
function CouponField({ subtotalCents, applied, onApply, onRemove }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  // The total can change while a code is applied (going back a step), and
  // a percentage is worth a different amount then.
  useEffect(() => {
    if (!applied) return;
    let cancelled = false;

    previewCoupon(applied.code, subtotalCents)
      .then((fresh) => !cancelled && onApply(fresh))
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        onRemove();
      });

    return () => {
      cancelled = true;
    };
    // Only re-run when the amount changes, not when the applied object is
    // replaced by the response above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotalCents]);

  async function handleApply() {
    setChecking(true);
    setError(null);
    try {
      onApply(await previewCoupon(code, subtotalCents));
      setCode('');
    } catch (err) {
      setError(err.message);
    } finally {
      setChecking(false);
    }
  }

  if (applied) {
    return (
      <div className="coupon-applied">
        <span>
          <strong>{applied.code}</strong>
          {applied.description && <small>{applied.description}</small>}
        </span>
        <button type="button" className="link-button" onClick={onRemove}>
          {t('coupon.remove')}
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button type="button" className="link-button coupon-toggle" onClick={() => setOpen(true)}>
        {t('coupon.haveCode')}
      </button>
    );
  }

  return (
    <div className="coupon-field">
      <div className="coupon-input-row">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={t('coupon.codePlaceholder')}
          aria-label={t('coupon.codePlaceholder')}
          // Enter would otherwise submit the order form.
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleApply();
            }
          }}
        />
        <button type="button" onClick={handleApply} disabled={!code.trim() || checking}>
          {checking ? '...' : t('coupon.apply')}
        </button>
      </div>
      {error && <p className="coupon-error">{error}</p>}
    </div>
  );
}

export default CouponField;
